/**
 * Members Service
 *
 * Handles space membership operations including adding, removing,
 * and managing member roles and permissions.
 */

import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { membersCollection } from '../lib/firestore-collections';
import type { SpaceMember, MemberRole, UpdateMemberInput } from '../types/firestore';

/**
 * Get all members of a space
 * Only active space members can view other members
 */
export async function getSpaceMembers(spaceId: string): Promise<SpaceMember[]> {
  const q = query(
    membersCollection,
    where('spaceId', '==', spaceId),
    where('status', '==', 'active'),
    orderBy('joinedAt', 'asc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  })) as SpaceMember[];
}

/**
 * Get a specific member's details
 */
export async function getMember(
  userId: string,
  spaceId: string
): Promise<SpaceMember | null> {
  const memberId = `${userId}_${spaceId}`;
  const docRef = doc(membersCollection, memberId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  } as SpaceMember;
}

/**
 * Get current user's membership in a space
 */
export async function getCurrentUserMembership(
  spaceId: string
): Promise<SpaceMember | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }

  return getMember(user.uid, spaceId);
}

/**
 * Add a member to a space
 * Used by invitation system when invitation is accepted
 */
export async function addMember(
  spaceId: string,
  userId: string,
  email: string,
  role: Exclude<MemberRole, 'owner'>,
  invitedBy: string,
  displayName?: string,
  photoURL?: string
): Promise<SpaceMember> {
  const memberId = `${userId}_${spaceId}`;
  const memberDocRef = doc(membersCollection, memberId);

  const memberData: Omit<SpaceMember, 'id'> = {
    spaceId,
    userId,
    email,
    displayName,
    photoURL,
    role,
    permissions: getDefaultPermissions(role),
    status: 'active',
    joinedAt: serverTimestamp() as Timestamp,
    invitedBy,
  };

  await setDoc(memberDocRef, memberData);

  return {
    id: memberId,
    ...memberData,
    joinedAt: Timestamp.now(),
  } as SpaceMember;
}

/**
 * Update member role or permissions
 * Only owner and admins can update members
 */
export async function updateMember(
  userId: string,
  spaceId: string,
  input: UpdateMemberInput
): Promise<void> {
  const memberId = `${userId}_${spaceId}`;
  const memberDocRef = doc(membersCollection, memberId);

  const updateData: any = {};

  if (input.role) {
    updateData.role = input.role;
    // Update permissions based on new role if not explicitly provided
    if (!input.permissions) {
      updateData.permissions = getDefaultPermissions(input.role);
    }
  }

  if (input.permissions) {
    const currentMember = await getMember(userId, spaceId);
    if (currentMember) {
      updateData.permissions = {
        ...currentMember.permissions,
        ...input.permissions,
      };
    }
  }

  if (input.status) {
    updateData.status = input.status;
    if (input.status === 'removed') {
      updateData.removedAt = serverTimestamp();
    }
  }

  await updateDoc(memberDocRef, updateData);
}

/**
 * Remove a member from a space (soft delete)
 * Only owner and admins can remove members
 */
export async function removeMember(userId: string, spaceId: string): Promise<void> {
  await updateMember(userId, spaceId, {
    status: 'removed',
  });
}

/**
 * Permanently delete a member
 * Use with caution - removes member from Firestore
 */
export async function permanentlyDeleteMember(
  userId: string,
  spaceId: string
): Promise<void> {
  const memberId = `${userId}_${spaceId}`;
  const memberDocRef = doc(membersCollection, memberId);
  await deleteDoc(memberDocRef);
}

/**
 * Update member's last active timestamp
 * Called when member performs any action in the space
 */
export async function updateMemberActivity(spaceId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    return;
  }

  const memberId = `${user.uid}_${spaceId}`;
  const memberDocRef = doc(membersCollection, memberId);

  try {
    await updateDoc(memberDocRef, {
      lastActiveAt: serverTimestamp(),
    });
  } catch (error) {
    // Silently fail if member doesn't exist or can't be updated
    console.warn('Failed to update member activity:', error);
  }
}

/**
 * Check if user has a specific permission in a space
 */
export async function hasPermission(
  spaceId: string,
  permission: keyof SpaceMember['permissions']
): Promise<boolean> {
  const membership = await getCurrentUserMembership(spaceId);
  if (!membership) {
    return false;
  }

  return membership.permissions[permission];
}

/**
 * Check if user is owner or admin of a space
 */
export async function isOwnerOrAdmin(spaceId: string): Promise<boolean> {
  const membership = await getCurrentUserMembership(spaceId);
  if (!membership) {
    return false;
  }

  return membership.role === 'owner' || membership.role === 'admin';
}

/**
 * Get all spaces where user is a member
 */
export async function getUserMemberships(): Promise<SpaceMember[]> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const q = query(
    membersCollection,
    where('userId', '==', user.uid),
    where('status', '==', 'active'),
    orderBy('joinedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  })) as SpaceMember[];
}

/**
 * Get member count for a space
 */
export async function getMemberCount(spaceId: string): Promise<number> {
  const members = await getSpaceMembers(spaceId);
  return members.length;
}

/**
 * Get default permissions based on role
 */
function getDefaultPermissions(role: MemberRole): SpaceMember['permissions'] {
  switch (role) {
    case 'owner':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: true,
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: true,
        canInviteMembers: true,
        canManageMembers: true,
        canChangeSettings: true,
      };
    case 'admin':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: true,
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: true,
        canInviteMembers: true,
        canManageMembers: true,
        canChangeSettings: false,
      };
    case 'member':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: false,
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: false,
        canInviteMembers: false,
        canManageMembers: false,
        canChangeSettings: false,
      };
    case 'viewer':
      return {
        canCreateNotes: false,
        canEditNotes: false,
        canDeleteNotes: false,
        canCreateEvents: false,
        canEditEvents: false,
        canDeleteEvents: false,
        canInviteMembers: false,
        canManageMembers: false,
        canChangeSettings: false,
      };
  }
}
